import moment from 'moment';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import withStyles from 'react-jss';
import styles from './container-card.styles';
import SyncIcon from 'src/components/common/icons/SyncIcon';
import classnames from 'classnames';
import TrackingShipView from 'src/components/pages/Container/TrackingShipView';
import { getDuration } from 'src/utils/utils';
import { SHIPMENT_TYPE, STATUS_LABELS } from 'src/components/pages/Container/ContainerUtils';
import { StylesProps, RouterProps, ShipmentTypes } from 'src/Library/Types';
import { SEContainerCardData } from '../../SE.interfaces';
import { colorStatusMapping } from 'src/colorScheme/containerStatus';
import { Checkbox, Col, Icon, Modal, Tag, Tooltip } from 'antd';
import { stops } from 'src/Library/Helper';
import { getAirCargoDetailsRoute, getContainerDetailsRoute } from 'src/routing/utils';
import { SETrackingCardData } from '../../SE.Types';
import DeleteIcon from 'src/assets/images/delete_icon.svg';

interface SETrackingCardProps extends StylesProps<ReturnType<typeof styles>>, RouterProps {
  cardType: ShipmentTypes;
  cardData: SETrackingCardData;
  handleOpenTracking: () => void;
  refreshCard: () => void;
  deleteCard: () => void;
  resetContainer: () => void;
  validContainerTypes: Record<string, string>;
  isAwb: boolean;
  addToBulkSelectedContainers: (cardData: SETrackingCardData) => void;
  removeFromBulkSelectedContainers: (cardData: SETrackingCardData) => void;
}
interface StateProps {
  disableRefresh?: number | boolean;
  refreshAt?: number;
}

const ContainerCard = (props: SETrackingCardProps) => {
  const { cardData, classes, handleOpenTracking, validContainerTypes, cardType, isAwb } = props;
  const [state, setState] = React.useState<StateProps>({});
  const history = useHistory();
  const { disableRefresh, refreshAt } = state;

  const isHBLPresent = cardData?.trackingType === SHIPMENT_TYPE.HBL;

  function processData() {
    const { cardData } = props;
    const refreshRequestedAt = cardData?.refreshRequestedAt;
    const duration = getDuration(refreshRequestedAt);
    const disableRefresh = cardData?.disableRefreshRequest;
    setState({
      disableRefresh,
      refreshAt: duration,
    });
  }

  React.useEffect(() => {
    processData();
  }, []);

  function componentDidUpdate(prevProps, prevState) {
    const refreshRequestedAt = cardData?.refreshRequestedAt;
    const duration = getDuration(refreshRequestedAt);
    const disableRefresh = cardData?.disableRefreshRequest;
    if (
      prevState.disableRefresh !== disableRefresh ||
      (duration && duration > 5 && disableRefresh)
    ) {
      setState({
        disableRefresh,
        refreshAt: duration,
      });
    }
  }

  function isCardArchived() {
    return Boolean(cardData?.isArchived);
  }

  function isBLCard() {
    return cardType === 'BL';
  }

  const renderShippingLine = () => {
    if (cardData?.shippingLineId === 'UNDEFINED' || cardData?.shippingLineName === 'undefined')
      return;
    return (
      <Tooltip
        title={cardData?.shippingLineName}
        placement="topLeft"
        className={classes.shippingLine}
      >
        {cardData?.logoUrl ? <img src={cardData.logoUrl} /> : cardData?.shippingLineName}
      </Tooltip>
    );
  };

  const showCardData = () => {
    const isTrackingPresent = cardData?.isTrackingPresent;
    return (
      isManuallyTracked() || (isTrackingPresent && !(disableRefresh && refreshAt && refreshAt < 5))
    );
  };

  const isManuallyTracked = () => {
    const { cardData } = props;
    return !!cardData?.isManuallyTracked;
  };

  const renderTag = () => {
    if (!showCardData() || !cardData?.tag) return <div style={{ width: '10%' }} />;
    const className = cardData.tag === 'DELAYED' ? classes.error : classes.success;
    const style: React.CSSProperties = {
      borderColor: colorStatusMapping[cardData?.tag],
      background: colorStatusMapping[cardData?.tag],
      cursor: 'default',
      height: 22,
    };

    return <Tag {...{ style, className }}>{STATUS_LABELS[cardData.tag]}</Tag>;
  };

  const isFetchingData = () => {
    const { cardData } = props;
    const isTrackingPresent = cardData?.isTrackingPresent;
    const isTrackingFailed = cardData?.isTrackingFailed;
    // const isTrackingDisabled = cardData?.trackAfterShipmentTaskApproval;
    if (isManuallyTracked()) {
      return false;
    }
    if (isTrackingPresent) {
      return disableRefresh && refreshAt && refreshAt < 5;
    }
    return disableRefresh || !isTrackingFailed;
  };

  const isTrackingFailed = () => {
    const isTrackingPresent = cardData?.isTrackingPresent;
    const isTrackingFailed = cardData?.isTrackingFailed;
    if (isManuallyTracked()) {
      return false;
    }
    return !isTrackingPresent && isTrackingFailed && !disableRefresh;
  };

  const renderFetchingData = () => {
    if (!isFetchingData() || cardData?.trackAfterShipmentTaskApproval) {
      return null;
    }
    const { classes } = props;
    return (
      <div className={classes.renderFetch}>
        <span style={{ color: '#51B841' }}>
          <Icon type="hourglass" />
          <span className={'loadingDots'} style={{ marginLeft: 8, color: '#51B841' }}>
          Fetching Data{' '}
          </span>
        </span>
      </div>
    );
  };

  const renderTrackingHoldMessage = () => {
    if (!cardData?.trackAfterShipmentTaskApproval || cardData?.isTrackingPresent) {
      return null;
    }

    return (
      <div className={classes.trackingFailed}>
        <Icon type="info-circle" theme="filled" />
        <span style={{ marginLeft: 8, color: '#EA4D4E' }}>
          {"Tracking is on hold"}
        </span>
      </div>
    );
  }

  const renderTrackingFailed = () => {
    if (!isTrackingFailed() || cardData?.trackAfterShipmentTaskApproval) {
      return null;
    }
    return (
      <div className={classes.trackingFailed}>
        <Icon type="info-circle" theme="filled" />
        <span style={{ marginLeft: 8, color: '#EA4D4E' }}>
          {cardData?.trackingFetchFailureMessage || 'Something went wrong'}
        </span>
      </div>
    );
  };

  const renderShip = () => {
    return (
      <TrackingShipView
        cardData={cardData}
        isTrackingFailed={isTrackingFailed()}
        isArchived={isCardArchived()}
        isAWB={isAwb}
      />
    );
  };

  const renderTracking = () => {
    if (!showCardData()) {
      return null;
    }
    return (
      <div
        style={{ cursor: 'pointer', width: '50%' }}
        onClick={isAwb ? handleShipmentDetail : handleOpenTracking}
      >
        {renderShip()}
      </div>
    );
  };

  const handleRefresh = async (event) => {
    stops(event);
    props.refreshCard();
  };

  const handleReset = async (event) => {
    stops(event);
    Modal.confirm({
      title: `Are you sure?`,
      content: `Resetting tracking will erase all existing data and start tracking the container's latest journey. Are you sure you want to proceed?`,
      okText: 'Reset',
      onOk: () => {
        props.resetContainer()
      },
      onCancel() {},
    });
  }

  const handleDelete = async (event) => {
    stops(event);
    Modal.confirm({
      title: `Delete Tracking`,
      content: `This will delete the selected shipment tracking. Do you wish to proceed?`,
      okText: 'Confirm',
      onOk: () => props.deleteCard(),
      onCancel() {},
    });
  }

  const renderReset = () => {
    if (isAwb) return null;
    if (isFetchingData() || isManuallyTracked()) return null;
    if (!cardData?.allowContainerReset) {
      return null;
    }
      return (
        <Tooltip title={'Reset Tracking'}>
          <Icon type="reload"
            onClick={handleReset}
            style={{
              fontSize: '16px',
              color: '#595959',
              cursor: 'pointer',
              marginRight: '7px',
            }}
          />
        </Tooltip>
      );
  };

  const renderReload = () => {
    if (isAwb) return null;
    if (isFetchingData() || isCardArchived() || isManuallyTracked()) return null;
    if (!disableRefresh || (refreshAt && refreshAt > 5)) {
      return (
        <Tooltip title={'Retry'}>
          <SyncIcon
            onClick={handleRefresh}
            style={{
              fontSize: '17px',
              color: '#595959',
              cursor: 'pointer',
              marginRight: '7px',
            }}
          />
        </Tooltip>
      );
    }
    return null;
  };

  const renderDelete = () => {
    if (isAwb) return null;
    const { trackingType} = cardData;
    if (!(isHBLPresent || isBLCard()) && trackingType) return null;
    if (isCardArchived()) return null;
    return (
      <Tooltip title={'Delete'}>
        <img
          src={DeleteIcon}
          width={15}
          style={{
            marginRight: '10px',
            cursor: 'pointer',
          }}
          onClick={handleDelete}
        />
      </Tooltip>
    );
  };

  const renderCompletionReason = () => {
    return (
      cardData?.completionReason && (
        <span style={{ marginRight: 8, fontSize: 10 }}>
          <Icon type="info-circle" style={{ marginRight: '5px' }} />
          {cardData.completionReason}
        </span>
      )
    );
  };

  const renderUpdatedAT = () => {
    if (!cardData?.lastSuccessfulTrackingStatusUpdatedAt) {
      return <></>;
    }

    return (
      <span className={classes.updatedAt}>
        {`Updated: ${moment(cardData?.lastSuccessfulTrackingStatusUpdatedAt).fromNow()}`}
      </span>
    );
  };

  const renderBlBookingNumber = (title: string, value: string) => {
    return (
      <div className={classes.flexRow}>
        <span className={classes.title}>{title}</span>
        <span className={classes.value}>{value}</span>
      </div>
    );
  };

  const renderMetaAWB = (leftChilds: React.ReactElement[]) => {
    leftChilds.push(renderBlBookingNumber('AWB#', (cardData as any)?.awbNumber || '-'));
  };

  const renderMetaBL = (leftChilds: React.ReactElement[]) => {
    leftChilds.push(
      renderBlBookingNumber(
        isHBLPresent ? 'HBL#' : 'BL#',
        (isHBLPresent ? cardData?.hblNumber : cardData?.blNumber) || '-'
      )
    );
    leftChilds.push(renderBlBookingNumber('Booking#', cardData?.bookingNumber || '-'));
    if (cardData?.linkedBlNumber) {
      leftChilds.push(renderBlBookingNumber('Linked BL Number#', cardData?.linkedBlNumber || '-'));
    }
  };

  const isShipmentCard = () => {
    return cardType === 'SHIPMENT';
  };

  const isContainerCard = () => {
    return cardType === 'CONTAINER';
  };

  const handleShipmentDetail = () => {
    const shipmentType =
      isShipmentCard() || isContainerCard() ? 'CN' : isHBLPresent ? 'HBL' : cardType;

    if (isAwb) {
      history.push(getAirCargoDetailsRoute('airCargo', cardData.id), {
        from: 'shipmentExecutionContainers',
      });
      return;
    }
    history.push(getContainerDetailsRoute(cardData.executionId, shipmentType), {
      from: 'shipmentExecutionContainers',
    });
  };

  const renderViewTracking = () => {
    return (
      <span className={classes.viewTrackingLink} onClick={() => handleShipmentDetail()}>
        View Tracking
      </span>
    );
  };

  const renderMeta = () => {
    const rightChilds: React.ReactElement[] = [];
    const leftChilds: React.ReactElement[] = [];

    if (isAwb) {
      renderMetaAWB(leftChilds);
    } else {
      renderMetaBL(leftChilds);
    }
    isCardArchived() && rightChilds.push(renderCompletionReason());
    rightChilds.push(renderViewTracking());
    rightChilds.push(renderUpdatedAT());

    return (
      <div className={classes.bottomRow}>
        <span className={classes.flexRow}>{leftChilds}</span>
        <span className={classes.flexRow}>{rightChilds}</span>
      </div>
    );
  };

  const getCardNumber = () => {
    if (isHBLPresent) {
      return cardData?.hblNumber;
    }
    if (isBLCard()) {
      return cardData?.blNumber || cardData?.bookingNumber;
    }
    return (
      (cardData as SEContainerCardData)?.containerNumber ||
      cardData?.blNumber ||
      cardData?.bookingNumber ||
      cardData?.hblNumber ||
      (cardData as any)?.awbNumber
    );
  };

  const renderCheckBox = () => {
    return (
      cardData as SEContainerCardData)?.containerNumber && 
      !(cardData as SEContainerCardData)?.isArchived &&
      !((cardData as SEContainerCardData)?.blNumber || (cardData as SEContainerCardData)?.bookingNumber) &&
      <span
        style={{marginRight: '5px'}}
      >
        <Checkbox onChange={(e) => {
            if (e.target.checked) {
              props.addToBulkSelectedContainers(cardData);
            } else {
              props.removeFromBulkSelectedContainers(cardData);
            }
          }
        }>
        </Checkbox>
      </span>
  }

  const renderReference = () => {
    const { containerSize, containerType } = cardData as SEContainerCardData;
    return (
      <div className={classes.referenceDetails}>
        <span
          className={classes.reference}
          style={{fontSize: '14px'}}
        >
          {renderCheckBox()}
          <span onClick={isAwb ? handleShipmentDetail : handleOpenTracking}>
          {getCardNumber()}
          </span>
        </span>
        <span className={classes.containerSize}>
          {containerSize} {containerType && validContainerTypes[containerType]}
        </span>
      </div>
    );
  };

  const renderCard = () => {
    return (
      <div className={classes.flexRow}>
        <div className={classes.leftContent}>
          {renderReference()}
          {renderTag()}
          {renderShippingLine()}
          {renderFetchingData()}
          {renderTrackingFailed()}
          {renderTrackingHoldMessage()}
          {renderTracking()}
          
        </div>
        {renderReload()}
        {renderReset()}
        {renderDelete()}
      </div>
    );
  };
  return (
    <div className={classnames(classes.parent, isCardArchived() ? classes.archived : null)}>
      {renderCard()}
      {renderMeta()}
    </div>
  );
};

const ContainerCardStyled = withStyles(styles)(ContainerCard);
export default ContainerCardStyled;
